import React, { useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { OfflineProvider } from "./context/OfflineContext";
import { usePageTracking } from "./utils/analytics";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Lessons from "./pages/Lessons";
import LessonView from "./pages/LessonView";
import Profile from "./pages/Profile";
import Achievements from "./pages/Achievements";
import Flashcards from "./pages/Flashcards";
import Practice from "./pages/Practice";
import Stories from "./pages/Stories";
import StoryView from "./pages/StoryView";
import Leaderboard from "./pages/Leaderboard";
import MedicalGerman from "./pages/MedicalGerman";
import OfflineSettings from "./pages/OfflineSettings";
import FreeChat from "./pages/FreeChat";
import AdminDashboard from "./pages/AdminDashboard";
import PflegeTraining from "./pages/PflegeTraining";
import PronunciationHistory from "./pages/PronunciationHistory";
import ExamSimulation from "./pages/ExamSimulation";
import ExamTrainer from "./pages/ExamTrainer";
import CulturalLearning from "./pages/CulturalLearning";
import MenschenA1 from "./pages/MenschenA1";
import MenschenA12 from "./pages/MenschenA12";
import MenschenA21 from "./pages/MenschenA21";
import MenschenA22 from "./pages/MenschenA22";
import VoiceDiary from "./pages/VoiceDiary";
import Bibliothek from "./pages/Bibliothek";
import MyProgress from "./pages/MyProgress";

// Register service worker for offline mode
const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
    } catch (error) {
    }
  }
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Public Route - redirects to dashboard if already authenticated
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router with session_id detection - MUST check BEFORE any route logic
function AppRouter() {
  const location = useLocation();
  usePageTracking();
  
  // Check URL fragment for session_id FIRST (synchronously during render)
  // This prevents race conditions with ProtectedRoute
  // Check both location.hash and window.location.hash for reliability
  const hash = location.hash || window.location.hash || '';
  
  // Also check URL search params in case session_id is passed differently
  const searchParams = new URLSearchParams(location.search);
  const hasSessionId = hash.includes('session_id=') || searchParams.has('session_id');
  
  if (hasSessionId) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPassword />
        </PublicRoute>
      } />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/lessons" element={
        <ProtectedRoute>
          <Lessons />
        </ProtectedRoute>
      } />
      <Route path="/lesson/:lessonId" element={
        <ProtectedRoute>
          <LessonView />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/achievements" element={
        <ProtectedRoute>
          <Achievements />
        </ProtectedRoute>
      } />
      <Route path="/flashcards" element={
        <ProtectedRoute>
          <Flashcards />
        </ProtectedRoute>
      } />
      <Route path="/practice/:lessonId" element={
        <ProtectedRoute>
          <Practice />
        </ProtectedRoute>
      } />
      <Route path="/stories" element={
        <ProtectedRoute>
          <Stories />
        </ProtectedRoute>
      } />
      <Route path="/story/:storyId" element={
        <ProtectedRoute>
          <StoryView />
        </ProtectedRoute>
      } />
      <Route path="/leaderboard" element={
        <ProtectedRoute>
          <Leaderboard />
        </ProtectedRoute>
      } />
      <Route path="/medical-german" element={
        <ProtectedRoute>
          <MedicalGerman />
        </ProtectedRoute>
      } />
      <Route path="/free-chat" element={
        <ProtectedRoute>
          <FreeChat />
        </ProtectedRoute>
      } />
      <Route path="/offline-settings" element={
        <ProtectedRoute>
          <OfflineSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/pflege-training" element={
        <ProtectedRoute>
          <PflegeTraining />
        </ProtectedRoute>
      } />
      <Route path="/pronunciation-history" element={
        <ProtectedRoute>
          <PronunciationHistory />
        </ProtectedRoute>
      } />
      <Route path="/exam-simulation" element={
        <ProtectedRoute>
          <ExamSimulation />
        </ProtectedRoute>
      } />
      <Route path="/exam-trainer" element={
        <ProtectedRoute>
          <ExamTrainer />
        </ProtectedRoute>
      } />
      <Route path="/cultural-learning" element={
        <ProtectedRoute>
          <CulturalLearning />
        </ProtectedRoute>
      } />
      <Route path="/menschen-a1" element={
        <ProtectedRoute>
          <MenschenA1 />
        </ProtectedRoute>
      } />
      <Route path="/menschen-a12" element={
        <ProtectedRoute>
          <MenschenA12 />
        </ProtectedRoute>
      } />
      <Route path="/menschen-a21" element={
        <ProtectedRoute>
          <MenschenA21 />
        </ProtectedRoute>
      } />
      <Route path="/menschen-a22" element={
        <ProtectedRoute>
          <MenschenA22 />
        </ProtectedRoute>
      } />
      <Route path="/bibliothek" element={
        <ProtectedRoute>
          <Bibliothek />
        </ProtectedRoute>
      } />
      <Route path="/voice-diary" element={
        <ProtectedRoute>
          <VoiceDiary />
        </ProtectedRoute>
      } />
      <Route path="/my-progress" element={
        <ProtectedRoute>
          <MyProgress />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Register service worker on mount
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <OfflineProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </OfflineProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
